import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getDocument,
  getCollection,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
} from '@/lib/firebase/firestore'
import type { CatalogAddonDocument } from '@/types'
import type { AddonFormValues } from '../types'

const addonsCol = () => typedCollection<CatalogAddonDocument>('catalogAddons')

type AddonCreatePayload = Omit<CatalogAddonDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

export const adminAddonsService = {
  async createAddon(data: AddonFormValues): Promise<string> {
    const payload: AddonCreatePayload = {
      label: data.label.trim(),
      pricingType: data.pricingType,
      ...(data.price !== '' && { price: data.price }),
      ...(data.priceMax !== '' && { priceMax: data.priceMax }),
      ...(data.pricingNote.trim() && { pricingNote: data.pricingNote.trim() }),
      selectionMode: data.selectionMode,
      pricingMode: data.pricingMode,
      rate: data.rate !== '' ? data.rate : 0,
      ...(data.maxQty !== '' && { maxQty: data.maxQty }),
      // Required array fields — managed by request form system
      appliesToCategories: [],
      appliesToOfferings: [],
      isPublished: data.isPublished,
      order: data.order,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(addonsCol(), payload as unknown as CatalogAddonDocument)
    return ref.id
  },

  async updateAddon(id: string, data: Partial<AddonFormValues>): Promise<void> {
    const ref = typedDoc<CatalogAddonDocument>('catalogAddons', id)
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }

    if (data.label !== undefined) updates.label = data.label.trim()
    if (data.pricingType !== undefined) updates.pricingType = data.pricingType
    if (data.price !== undefined) updates.price = data.price !== '' ? data.price : null
    if (data.priceMax !== undefined) updates.priceMax = data.priceMax !== '' ? data.priceMax : null
    if (data.pricingNote !== undefined) updates.pricingNote = data.pricingNote.trim()
    if (data.selectionMode !== undefined) updates.selectionMode = data.selectionMode
    if (data.pricingMode !== undefined) updates.pricingMode = data.pricingMode
    if (data.rate !== undefined) updates.rate = data.rate !== '' ? data.rate : 0
    if (data.maxQty !== undefined) updates.maxQty = data.maxQty !== '' ? data.maxQty : null
    if (data.isPublished !== undefined) updates.isPublished = data.isPublished
    if (data.order !== undefined) updates.order = data.order
    // appliesToCategories / appliesToOfferings: NOT updated here — managed by request form system

    await updateDocument(ref, updates as unknown as CatalogAddonDocument)
  },

  async deleteAddon(id: string): Promise<void> {
    const ref = typedDoc<CatalogAddonDocument>('catalogAddons', id)
    await deleteDocument(ref)
  },

  async getAllAddons(): Promise<Array<CatalogAddonDocument & { id: string }>> {
    return getCollection(addonsCol(), orderBy('order', 'asc'))
  },

  async getAddonById(id: string): Promise<(CatalogAddonDocument & { id: string }) | null> {
    const ref = typedDoc<CatalogAddonDocument>('catalogAddons', id)
    const data = await getDocument(ref)
    return data ? { ...data, id } : null
  },
}
